/**
 * useDraft — small hook that wraps the /api/drafts endpoints.
 *
 * Usage on a create page:
 *   const { draftId, saveDraft, loadDraft, discardDraft, saving } = useDraft("trip");
 *
 *   // Save current form state as a draft
 *   await saveDraft(form);
 *
 *   // After successful publish, drop the draft if any
 *   await discardDraft();
 *
 * Auto-save:
 *   const { autoSaveStatus } = useDraft("trip", { onLoad, autoSaveData: form });
 *   // autoSaveStatus: "idle" | "saving" | "saved"
 *   // Saves silently 2.5s after the latest change, skipping empty/initial payloads.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";

const AUTOSAVE_DEBOUNCE_MS = 2500;

export function useDraft(type, { onLoad, autoSaveData, autoSaveEnabled = true } = {}) {
  const [params, setParams] = useSearchParams();
  const initialDraftId = params.get("draft") || null;
  const [draftId, setDraftId] = useState(initialDraftId);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState("idle"); // idle | saving | saved
  const lastSavedRef = useRef(null);
  const draftIdRef = useRef(initialDraftId);
  const timerRef = useRef(null);

  // Load draft when ?draft= present
  useEffect(() => {
    if (!initialDraftId || loaded || !onLoad) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(`${API}/drafts/${initialDraftId}`, { headers });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || data.type !== type) return;
        onLoad(data.payload || {});
        // Treat the freshly-loaded snapshot as already-saved so the autosave
        // effect doesn't immediately re-persist the same content.
        lastSavedRef.current = JSON.stringify(data.payload || {});
        setLoaded(true);
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [initialDraftId, type, loaded, onLoad]);

  const saveDraft = useCallback(async (payload, { silent } = {}) => {
    if (saving) return null;
    setSaving(true);
    try {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      const currentId = draftIdRef.current;
      const res = await fetch(`${API}/drafts`, {
        method: "POST", headers,
        body: JSON.stringify({ draft_id: currentId, type, payload }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.draft_id && data.draft_id !== currentId) {
          draftIdRef.current = data.draft_id;
          setDraftId(data.draft_id);
          // Reflect in URL so refresh keeps editing the same draft
          const next = new URLSearchParams(params);
          next.set("draft", data.draft_id);
          setParams(next, { replace: true });
        }
        lastSavedRef.current = JSON.stringify(payload);
        if (!silent) toast.success("Draft saved");
        return data.draft_id;
      }
      if (!silent) toast.error("Couldn't save draft");
      return null;
    } catch (e) {
      if (!silent) toast.error("Couldn't save draft");
      return null;
    } finally {
      setSaving(false);
    }
  }, [type, params, setParams, saving]);

  // Keep ref in sync when draftId state changes
  useEffect(() => { draftIdRef.current = draftId; }, [draftId]);

  // Heuristic: payload is "meaningful" if any string/array field is non-empty.
  // Avoids autosaving on first render of an empty form.
  const isMeaningful = (payload) => {
    if (!payload || typeof payload !== "object") return false;
    for (const v of Object.values(payload)) {
      if (typeof v === "string" && v.trim().length > 0) return true;
      if (Array.isArray(v) && v.length > 0) return true;
      if (typeof v === "number" && v > 0) return true;
    }
    return false;
  };

  // Auto-save: debounced, silent. Only runs when autoSaveData provided.
  useEffect(() => {
    if (!autoSaveEnabled || autoSaveData === undefined) return;
    // If we're still loading an existing draft, wait for it to land first.
    if (initialDraftId && !loaded) return;

    const serialized = JSON.stringify(autoSaveData);
    if (serialized === lastSavedRef.current) return;
    if (!isMeaningful(autoSaveData)) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setAutoSaveStatus("saving");
      const result = await saveDraft(autoSaveData, { silent: true });
      setAutoSaveStatus(result ? "saved" : "idle");
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [autoSaveData, autoSaveEnabled, initialDraftId, loaded, saveDraft]);

  const discardDraft = useCallback(async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const currentId = draftIdRef.current;
    if (!currentId) return;
    try {
      const headers = await authHeaders();
      await fetch(`${API}/drafts/${currentId}`, { method: "DELETE", headers });
    } catch (e) { /* ignore */ }
    draftIdRef.current = null;
    setDraftId(null);
    setAutoSaveStatus("idle");
    lastSavedRef.current = null;
  }, []);

  return {
    draftId,
    saving,
    saveDraft,
    discardDraft,
    autoSaveStatus,
    isExistingDraft: !!initialDraftId,
  };
}
